<template>
  <div class="modal-backdrop-custom" @click.self="$emit('fermer')">
    <div class="modal-card card shadow">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-0"><i class="bi bi-database me-1"></i>Données · {{ clinique.nom }}</h5>
          <small class="text-muted">Sauvegardes, export, réinitialisation, suppression et restauration.</small>
        </div>
        <button class="btn-close" @click="$emit('fermer')"></button>
      </div>

      <div class="card-body">
        <div v-if="!d" class="text-center py-4"><div class="spinner-border text-primary"></div></div>
        <template v-else>
          <div v-if="estSupprimee" class="alert alert-danger py-2">
            Clinique supprimée<span v-if="clinique.supprimeeLe"> le {{ dateHeure(clinique.supprimeeLe) }}</span>.
            Ses données peuvent être récupérées en restaurant la sauvegarde « avant-suppression » ci-dessous.
          </div>

          <!-- Volume -->
          <div class="row g-2 mb-3">
            <div class="col-6 col-md-3"><div class="border rounded p-2 h-100"><small class="text-muted">Enregistrements</small><div class="fs-5 fw-bold">{{ nombre(d.donnees.total) }}</div></div></div>
            <div class="col-6 col-md-3"><div class="border rounded p-2 h-100"><small class="text-muted">Comptes et configuration</small><div class="fs-5 fw-bold">{{ nombre(d.donnees.categories.comptes) }}</div></div></div>
            <div class="col-6 col-md-3"><div class="border rounded p-2 h-100"><small class="text-muted">Paramétrage (tarifs, services…)</small><div class="fs-5 fw-bold">{{ nombre(d.donnees.categories.referentiels) }}</div></div></div>
            <div class="col-6 col-md-3"><div class="border rounded p-2 h-100"><small class="text-muted">Activité (patients, caisse…)</small><div class="fs-5 fw-bold">{{ nombre(d.donnees.categories.activite) }}</div></div></div>
          </div>

          <!-- Actions -->
          <div class="d-flex flex-wrap gap-2 mb-3">
            <button class="btn btn-outline-primary" :disabled="enCours" @click="sauvegarder"><i class="bi bi-cloud-arrow-down me-1"></i>Faire une sauvegarde</button>
            <div v-if="principal" class="btn-group" role="group" aria-label="Exporter pour la clinique">
              <button class="btn btn-outline-success" :disabled="enCours || estSupprimee" title="Classeur Excel lisible : une feuille par activité" @click="exporter('excel')"><i class="bi bi-file-earmark-excel me-1"></i>Excel</button>
              <button class="btn btn-outline-danger" :disabled="enCours || estSupprimee" title="Relevé PDF à signer lors de la remise" @click="exporter('pdf')"><i class="bi bi-file-earmark-pdf me-1"></i>Relevé PDF</button>
              <button class="btn btn-outline-secondary" :disabled="enCours || estSupprimee" title="Archive complète (CSV, JSON, pièces jointes), sans mots de passe" @click="exporter('zip')"><i class="bi bi-file-earmark-zip me-1"></i>ZIP</button>
            </div>
            <template v-if="principal">
              <button class="btn btn-outline-warning" :disabled="enCours || estSupprimee" @click="ouvrir('reinitialisation')"><i class="bi bi-arrow-counterclockwise me-1"></i>Réinitialiser</button>
              <button class="btn btn-outline-danger ms-auto" :disabled="enCours || estSupprimee" @click="ouvrir('suppression')"><i class="bi bi-trash me-1"></i>Supprimer la clinique</button>
            </template>
          </div>
          <div v-if="!principal" class="alert alert-light border small py-2">
            <i class="bi bi-info-circle me-1"></i>Vous pouvez faire une sauvegarde avant une opération risquée.
            L'export des données (il contient les dossiers des patients) se fait par la clinique elle-même, dans
            « Protection des données » ; réinitialisation, restauration et suppression restent à l'administrateur principal.
          </div>

          <!-- Opération sensible -->
          <div v-if="op" class="border rounded p-3 mb-3" :class="op.type === 'suppression' ? 'border-danger' : 'border-warning'">
            <template v-if="op.type === 'reinitialisation'">
              <h6 class="fw-bold">Réinitialiser les données</h6>
              <p class="small mb-2">
                Par exemple à la fin de l'essai, pour démarrer « au propre » avec l'offre payante.
                <strong>Une sauvegarde complète est faite automatiquement avant</strong> : tout peut être restauré.
              </p>
              <div class="form-check">
                <input id="m1" v-model="op.mode" type="radio" value="ACTIVITE" class="form-check-input">
                <label for="m1" class="form-check-label">
                  <strong>Effacer l'activité seulement</strong> (patients, consultations, caisse, ventes, stock, hospitalisations…).
                  Les comptes, la configuration et le paramétrage (tarifs, services, produits, chambres, assurances) sont gardés.
                </label>
              </div>
              <div class="form-check mt-1">
                <input id="m2" v-model="op.mode" type="radio" value="COMPLET" class="form-check-input">
                <label for="m2" class="form-check-label">
                  <strong>Tout effacer sauf les comptes et la configuration</strong> : la clinique repart d'une installation neuve.
                </label>
              </div>
            </template>
            <template v-else-if="op.type === 'suppression'">
              <h6 class="fw-bold text-danger">Supprimer la clinique</h6>
              <p class="small mb-2">
                Efface toutes les données de la clinique, comptes utilisateurs compris. La fiche de la clinique et ses factures
                d'abonnement restent (comptabilité). <strong>Une sauvegarde complète est faite automatiquement avant</strong> et
                permet de tout récupérer.
              </p>
              <div v-if="statut !== 'SUSPENDU'" class="alert alert-warning py-2 small mb-2">Suspendez d'abord la clinique (bouton « Suspendre » de la liste).</div>
            </template>
            <template v-else-if="op.type === 'restauration'">
              <h6 class="fw-bold">Restaurer la sauvegarde {{ op.archive.nom }}</h6>
              <p class="small mb-2">
                Du {{ dateHeure(op.archive.creeLe) }} ({{ nombre(op.archive.lignes) }} enregistrements).
                Les enregistrements effacés depuis sont recréés, ceux qui existent reprennent leur valeur d'alors ;
                ce qui a été saisi après la sauvegarde est conservé. L'état actuel est sauvegardé juste avant.
              </p>
            </template>
            <template v-else-if="op.type === 'archive'">
              <h6 class="fw-bold">Supprimer la sauvegarde {{ op.archive.nom }}</h6>
              <p class="small mb-2">Cette copie sera définitivement perdue.</p>
            </template>

            <div class="row g-2 mt-1">
              <div v-if="op.type !== 'restauration' && op.type !== 'archive'" class="col-md-6">
                <label class="form-label small mb-1">Motif *</label>
                <input v-model.trim="op.motif" class="form-control form-control-sm" placeholder="Fin de l'essai, fin de contrat…">
              </div>
              <div class="col-md-6">
                <label class="form-label small mb-1">Pour confirmer, saisissez le nom de la clinique</label>
                <input v-model="op.confirmation" class="form-control form-control-sm" :placeholder="clinique.nom">
              </div>
            </div>
            <div class="d-flex justify-content-end gap-2 mt-2">
              <button class="btn btn-sm btn-outline-secondary" :disabled="enCours" @click="op = null">Annuler</button>
              <button class="btn btn-sm" :class="op.type === 'restauration' ? 'btn-primary' : 'btn-danger'" :disabled="enCours || !confirmationValide || (op.type === 'suppression' && statut !== 'SUSPENDU')" @click="executer">
                <span v-if="enCours" class="spinner-border spinner-border-sm me-1"></span>{{ libelleOperation }}
              </button>
            </div>
          </div>

          <div v-if="resultat" class="alert alert-success py-2 small">
            {{ resultat.message }}
            <ul v-if="resultat.avertissements?.length" class="mb-0 mt-1"><li v-for="(a, i) in resultat.avertissements" :key="i">{{ a }}</li></ul>
          </div>
          <div v-if="erreur" class="alert alert-danger py-2 small">{{ erreur }}</div>

          <!-- Sauvegardes -->
          <h6 class="fw-bold mt-2">Sauvegardes conservées sur le serveur</h6>
          <div class="table-responsive">
            <table class="table table-sm align-middle small mb-0">
              <thead><tr><th>Date</th><th>Motif</th><th>Par</th><th class="text-end">Enregistrements</th><th class="text-end">Taille</th><th></th></tr></thead>
              <tbody>
                <tr v-for="a in d.archives" :key="a.nom">
                  <td class="text-nowrap">{{ dateHeure(a.creeLe) }}</td>
                  <td>{{ a.motif || '—' }}<span v-if="!a.valide" class="badge bg-danger ms-1">Illisible</span></td>
                  <td>{{ a.creePar || '—' }}</td>
                  <td class="text-end">{{ nombre(a.lignes) }}</td>
                  <td class="text-end text-nowrap">{{ taille(a.taille) }}</td>
                  <td v-if="principal" class="text-end text-nowrap">
                    <button class="btn btn-sm btn-outline-secondary" title="Télécharger" @click="telecharger(a)"><i class="bi bi-download"></i></button>
                    <button v-if="a.valide && a.type === 'SAUVEGARDE'" class="btn btn-sm btn-outline-primary ms-1" title="Restaurer" :disabled="enCours" @click="ouvrir('restauration', a)"><i class="bi bi-arrow-repeat"></i></button>
                    <button class="btn btn-sm btn-outline-danger ms-1" title="Supprimer la sauvegarde" :disabled="enCours" @click="ouvrir('archive', a)"><i class="bi bi-x-lg"></i></button>
                  </td>
                </tr>
                <tr v-if="!d.archives.length"><td colspan="6" class="text-center text-muted py-3">Aucune sauvegarde pour le moment.</td></tr>
              </tbody>
            </table>
          </div>
          <p class="small text-muted mt-2 mb-0">
            Les sauvegardes contiennent des données médicales et les mots de passe chiffrés des comptes : ne les transmettez pas.
            Pour remettre ses données à une clinique : Excel (lisible), relevé PDF (à faire signer) et, si besoin, l'archive ZIP complète avec les pièces jointes. Aucun mot de passe ni clé n'y figure.
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'
import dayjs from 'dayjs'

export default {
  name: 'DonneesClinique',
  props: { clinique: { type: Object, required: true } },
  emits: ['fermer', 'modifiee'],
  data() {
    return { d: null, op: null, enCours: false, erreur: '', resultat: null, statut: this.clinique.statut }
  },
  computed: {
    /** Administrateur principal ; le gestionnaire voit le volume et fait des sauvegardes. */
    principal() { return !this.$store.getters.isPlatformGestionnaire },
    estSupprimee() { return this.statut === 'SUPPRIMEE' },
    confirmationValide() {
      if (!this.op) return false
      const nom = (this.op.confirmation || '').trim().toLowerCase() === (this.clinique.nom || '').trim().toLowerCase()
      const motif = ['restauration', 'archive'].includes(this.op.type) || (this.op.motif || '').length >= 3
      return nom && motif
    },
    libelleOperation() {
      return { reinitialisation: 'Sauvegarder puis réinitialiser', suppression: 'Sauvegarder puis supprimer', restauration: 'Restaurer', archive: 'Supprimer la sauvegarde' }[this.op?.type]
    },
  },
  created() { this.charger() },
  methods: {
    base() { return `plateforme/cliniques/${this.clinique.id}` },
    async charger() {
      try {
        this.d = (await api.custom(`${this.base()}/donnees`, 'GET')).data
      } catch (e) {
        this.erreur = messageErreur(e, 'Impossible de charger les données de la clinique.')
      }
    },
    ouvrir(type, archive = null) {
      this.erreur = ''
      this.resultat = null
      this.op = { type, archive, mode: 'ACTIVITE', motif: '', confirmation: '' }
    },
    async sauvegarder() {
      this.enCours = true
      this.erreur = ''
      this.resultat = null
      try {
        const r = (await api.post(`${this.base()}/archives`, { motif: 'manuelle' })).data
        this.d.archives = r.archives
        this.resultat = { message: `Sauvegarde ${r.archive} créée.` }
      } catch (e) {
        this.erreur = messageErreur(e, 'La sauvegarde a échoué.')
      } finally {
        this.enCours = false
      }
    },
    async exporter(format = 'zip') {
      const routes = { zip: 'export', excel: 'export-excel', pdf: 'export-pdf' }
      const extensions = { zip: 'zip', excel: 'xlsx', pdf: 'pdf' }
      this.enCours = true
      this.erreur = ''
      try {
        await api.telecharger(`${this.base()}/${routes[format]}`, `${this.clinique.slug || 'clinique'}-donnees-${dayjs().format('YYYYMMDD')}.${extensions[format]}`)
      } catch (e) {
        this.erreur = "L'export a échoué. Réessayez ; si le problème continue, consultez le journal du serveur."
      } finally {
        this.enCours = false
      }
    },
    async telecharger(a) {
      try {
        await api.telecharger(`${this.base()}/archives/${a.nom}`, `${this.clinique.slug || 'clinique'}-${a.nom}`)
      } catch (e) {
        notifier('Téléchargement impossible.')
      }
    },
    async executer() {
      const op = this.op
      this.enCours = true
      this.erreur = ''
      this.resultat = null
      try {
        let r
        if (op.type === 'reinitialisation') {
          r = (await api.post(`${this.base()}/reinitialisation`, { mode: op.mode, motif: op.motif, confirmation: op.confirmation })).data
          this.resultat = { message: `Réinitialisation faite : ${r.supprimees} enregistrement(s) effacé(s). Sauvegarde préalable : ${r.archive}.` }
        } else if (op.type === 'suppression') {
          r = (await api.post(`${this.base()}/suppression`, { motif: op.motif, confirmation: op.confirmation })).data
          this.statut = 'SUPPRIMEE'
          this.resultat = { message: `Clinique supprimée : ${r.supprimees} enregistrement(s) effacé(s). Sauvegarde préalable : ${r.archive}.` }
        } else if (op.type === 'restauration') {
          r = (await api.post(`${this.base()}/archives/${op.archive.nom}/restauration`, { confirmation: op.confirmation })).data
          if (this.statut === 'SUPPRIMEE') this.statut = 'SUSPENDU'
          this.resultat = {
            message: `Restauration faite : ${r.crees} enregistrement(s) recréé(s), ${r.misAJour} remis à jour, ${r.fichiers} pièce(s) jointe(s). État précédent sauvegardé : ${r.sauvegardeAvant}.`,
            avertissements: r.avertissements,
          }
        } else if (op.type === 'archive') {
          r = (await api.custom(`${this.base()}/archives/${op.archive.nom}`, 'DELETE', { confirmation: op.confirmation })).data
          this.resultat = { message: 'Sauvegarde supprimée.' }
        }
        this.op = null
        await this.charger()
        this.$emit('modifiee')
      } catch (e) {
        this.erreur = messageErreur(e, "L'opération a échoué : aucune donnée n'a été modifiée.")
      } finally {
        this.enCours = false
      }
    },
    nombre(n) { return new Intl.NumberFormat().format(n || 0) },
    taille(o) {
      if (!o) return '—'
      return o > 1048576 ? (o / 1048576).toFixed(1) + ' Mo' : Math.max(1, Math.round(o / 1024)) + ' ko'
    },
    dateHeure(d) { return d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—' },
  },
}
</script>

<style scoped>
.modal-backdrop-custom { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); z-index: 1050; display: flex; align-items: flex-start; justify-content: center; padding: 30px 16px; overflow-y: auto; }
.modal-card { width: 100%; max-width: 920px; border-radius: 12px; }
</style>
